import i18n from 'i18n-js';

import { getProductionBatchByProductionOperationId } from '@reducers/search';
import { productionBatchStatistics } from '@reducers/statistics';
import type { AppDispatch } from '@store/types';
import type { ProductionBatchStatistics } from '@interfaces/search';
import { scale } from '@components/frame';
import type { ScaleFunction } from '@components/operatorPanel/interfaces';
import { KEYS_TAGS, IMAGE_HANDLING_CONTEXT, DOCUMENT_ENTITY_TYPE } from '@components/imageHandling/common';
import type { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import type { AggregatedBatchTimeData, BatchSearchResult, EnrichedBatch, PrefillSource, TimeRowData } from '@v2/components/FinishJobModal/types';
import { BATCH_PAGE_SIZE, FINISH_JOB_THUMBNAIL_WIDTH, FINISH_JOB_THUMBNAIL_HEIGHT } from '@v2/components/FinishJobModal/constants';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

// Picks the thumbnail document for the job: this operation's favorite first, then any
// favorite, then the first image. Only true images qualify (PDFs/notes/other docs are skipped).
export const selectJobThumbnailDocument = (documents: ImageDocumentStoreItem[], productionOperationId: number): ImageDocumentStoreItem | null => {
  const images = documents.filter((doc) => !doc.isDocument && !doc.isPDF);
  if (!images.length) return null;

  // Favorite scoped to this operation wins, then any favorite, then the first image.
  const operationFavorite = images.find((doc) =>
    doc.links?.some((l) => l.isFavorite && l.scopeId === KEYS_TAGS.Operation && l.scopeReferenceId === productionOperationId)
  );
  const anyFavorite = images.find((doc) => doc.links?.some((l) => l.isFavorite));

  return operationFavorite ?? anyFavorite ?? images[0];
};

// Builds the <Image> context props for a job thumbnail document. Pure so it lives beside
// the selector and is unit-testable without the hook.
export const buildJobThumbnailImageProps = (document: ImageDocumentStoreItem | null, contextId: number, scaleFn: ScaleFunction = scale) => {
  const imageHash = document?.contentHash;
  if (!document || !imageHash) return null;

  const pageNumber = document.pageNumber ?? 1;
  const rotationAngle = document.customRotate?.[pageNumber] ?? 0;

  return {
    image: {
      documentId: document.id,
      imageHash,
      imageData: {
        isCropped: document.isCropped ?? false,
        // Documents expose extensionDerivative; fall back to the original when none was generated.
        extensionDerivative: document.extensionDerivative ?? document.extensionOriginal ?? undefined,
        pageNumber,
        rotationAngle,
        rotationAngleCrop: rotationAngle,
      },
      width: scaleFn(FINISH_JOB_THUMBNAIL_WIDTH),
      height: scaleFn(FINISH_JOB_THUMBNAIL_HEIGHT),
      contextId,
      contextCode: IMAGE_HANDLING_CONTEXT[DOCUMENT_ENTITY_TYPE.ProductionOperation],
      isProcessing: false,
      borderRadius: scaleFn(SCREEN_BORDER_RADIUS),
    },
    showImageControl: false,
    noOverlay: true,
    disableImageOverlay: true,
  };
};

export const fetchAllBatches = async (
  dispatch: AppDispatch,
  operationId: number,
  signal?: AbortSignal
): Promise<EnrichedBatch[]> => {
  const allBatches: EnrichedBatch[] = [];
  let skip = 0;

  const firstPage: BatchSearchResult = await dispatch(
    getProductionBatchByProductionOperationId({ id: operationId, filters: { top: BATCH_PAGE_SIZE, skip: 0 } })
  );

  if (!firstPage?.searchResult) return [];

  allBatches.push(...firstPage.searchResult);

  const totalCount = firstPage.totalResultCount ?? firstPage.searchResult.length;

  while (allBatches.length < totalCount) {
    if (signal?.aborted) return allBatches;

    skip += BATCH_PAGE_SIZE;
    const page: BatchSearchResult = await dispatch(
      getProductionBatchByProductionOperationId({ id: operationId, filters: { top: BATCH_PAGE_SIZE, skip } })
    );

    if (!page?.searchResult?.length) break;

    allBatches.push(...page.searchResult);
  }

  // Search index has unreliable time values — use statistics API for all time fields
  if (allBatches.length > 0 && !signal?.aborted) {
    const uniqueIds = [...new Set(allBatches.map((b) => b.productionBatchId))];
    const statsResults = await Promise.all(
      uniqueIds.map((id) => dispatch(productionBatchStatistics({ productionBatchId: id })))
    );

    const statsMap = new Map<number, ProductionBatchStatistics>();
    for (const result of statsResults) {
      if (result?.statistics) {
        statsMap.set(result.statistics.productionBatchId, result.statistics);
      }
    }

    for (const batch of allBatches) {
      const stats = statsMap.get(batch.productionBatchId);
      if (stats) {
        batch.scheduledSetupTimeInSeconds = stats.scheduledSetupTimeInSeconds ?? 0;
        batch.scheduledCycleTimeTotalInSeconds = stats.scheduledCycleTimeTotalInSeconds ?? 0;
        batch.idealSetupTimeInSeconds = stats.idealSetupTimeInSeconds ?? 0;
        batch.idealCycleTimeTotalInSeconds = stats.idealCycleTimeTotalInSeconds ?? 0;
        batch.paidSetupTimeInSeconds = stats.paidSetupTimeInSeconds ?? 0;
        batch.paidCycleTimeTotalInSeconds = stats.paidCycleTimeTotalInSeconds ?? 0;
        batch.actualSetupTimeInSeconds = stats.actualSetupTimeInSeconds ?? 0;
        batch.actualCycleTimeTotalInSeconds = stats.actualCycleTimeTotalInSeconds ?? 0;
      }
    }
  }

  return allBatches;
};

export const aggregateBatchTimeData = (batches: EnrichedBatch[]): AggregatedBatchTimeData | null => {
  if (!batches.length) return null;

  const seen = new Set<number>();
  const unique: EnrichedBatch[] = [];

  for (const batch of batches) {
    const id = batch.productionBatchId;
    if (!seen.has(id)) {
      seen.add(id);
      unique.push(batch);
    }
  }

  const result: AggregatedBatchTimeData = {
    scheduledSetupTimeInSeconds: 0,
    scheduledCycleTimeTotalInSeconds: 0,
    idealSetupTimeInSeconds: 0,
    idealCycleTimeTotalInSeconds: 0,
    paidSetupTimeInSeconds: 0,
    paidCycleTimeTotalInSeconds: 0,
    actualSetupTimeInSeconds: 0,
    actualCycleTimeTotalInSeconds: 0,
    batchNumbers: [],
    totalQuantity: 0,
  };

  for (const batch of unique) {
    result.scheduledSetupTimeInSeconds += batch.scheduledSetupTimeInSeconds ?? 0;
    result.scheduledCycleTimeTotalInSeconds += batch.scheduledCycleTimeTotalInSeconds ?? 0;
    result.idealSetupTimeInSeconds += batch.idealSetupTimeInSeconds ?? 0;
    result.idealCycleTimeTotalInSeconds += batch.idealCycleTimeTotalInSeconds ?? 0;
    result.paidSetupTimeInSeconds += batch.paidSetupTimeInSeconds ?? 0;
    result.paidCycleTimeTotalInSeconds += batch.paidCycleTimeTotalInSeconds ?? 0;
    result.actualSetupTimeInSeconds += batch.actualSetupTimeInSeconds ?? 0;
    result.actualCycleTimeTotalInSeconds += batch.actualCycleTimeTotalInSeconds ?? 0;
    result.totalQuantity += batch.quantity ?? batch.quantityReady ?? 0;

    if (batch.batchNumber) {
      result.batchNumbers.push(batch.batchNumber);
    }
  }

  return result;
};

// Resolves the prefilled Actual setup/cycle times and their provenance from aggregated batch data.
// Per-field fallback chain: batch actual → batch paid → operation-level fallback. The reported
// source is "paid" if either field used paid, "operation" if both fell back to operation, else "actual".
export const resolveActualTimePrefill = (
  aggregated: AggregatedBatchTimeData,
  fallback: { setup: number; cycle: number }
): { setup: number; cycle: number; source: PrefillSource } => {
  let setup: number;
  let cycle: number;
  let setupSource: PrefillSource = 'actual';
  let cycleSource: PrefillSource = 'actual';

  // Setup: actual → paid → operation
  if (aggregated.actualSetupTimeInSeconds > 0) {
    setup = aggregated.actualSetupTimeInSeconds;
  } else if (aggregated.paidSetupTimeInSeconds > 0) {
    setup = aggregated.paidSetupTimeInSeconds;
    setupSource = 'paid';
  } else {
    setup = fallback.setup;
    setupSource = 'operation';
  }

  // Cycle: actual → paid → operation
  if (aggregated.actualCycleTimeTotalInSeconds > 0) {
    cycle = aggregated.actualCycleTimeTotalInSeconds;
  } else if (aggregated.paidCycleTimeTotalInSeconds > 0) {
    cycle = aggregated.paidCycleTimeTotalInSeconds;
    cycleSource = 'paid';
  } else {
    cycle = fallback.cycle;
    cycleSource = 'operation';
  }

  let source: PrefillSource;
  if (setupSource === 'paid' || cycleSource === 'paid') {
    source = 'paid';
  } else if (setupSource === 'operation' && cycleSource === 'operation') {
    source = 'operation';
  } else {
    source = 'actual';
  }

  return { setup, cycle, source };
};

export const getProvenanceText = (
  batchData: AggregatedBatchTimeData | null,
  prefillSource: PrefillSource
): string => {
  if (prefillSource === 'error') {
    return i18n.t('Could not load batch data, estimated from job data');
  }

  if (!batchData || prefillSource === 'operation') {
    return i18n.t('No linked Batches, therefore estimated Actual prefilled with Paid');
  }

  const batchLabel = batchData.batchNumbers.length > 0
    ? `${i18n.t('Batch')} ${batchData.batchNumbers.join(', ')}`
    : i18n.t('Batch');

  if (prefillSource === 'paid') {
    return `${batchLabel}, ${i18n.t('estimated Actual prefilled with Paid')}`;
  }

  return batchLabel;
};

export const getSetupTimeRows = (
  batchData: AggregatedBatchTimeData | null,
  actualSeconds: number,
  fallbackScheduled: number
): TimeRowData[] => {
  // No linked batches: Scheduled/Ideal don't exist for un-batched jobs, and the operation-level
  // scheduled time IS the ERP "Paid" estimate. Show only Paid + Actual.
  if (!batchData) {
    return [
      { label: 'Paid', value: fallbackScheduled, isEditable: false },
      { label: 'Actual', value: actualSeconds, isEditable: true },
    ];
  }

  return [
    { label: 'Scheduled', value: batchData.scheduledSetupTimeInSeconds, isEditable: false },
    { label: 'Ideal', value: batchData.idealSetupTimeInSeconds, isEditable: false },
    { label: 'Paid', value: batchData.paidSetupTimeInSeconds, isEditable: false },
    { label: 'Actual', value: actualSeconds, isEditable: true },
  ];
};

export const getCycleTimeRows = (
  batchData: AggregatedBatchTimeData | null,
  actualSeconds: number,
  quantity: number,
  fallbackScheduled: number
): TimeRowData[] => {
  const perPiece = (total: number) => (quantity > 0 ? Math.round(total / quantity) : 0);

  // No linked batches: only Paid (= operation-level scheduled estimate) + Actual.
  if (!batchData) {
    return [
      { label: 'Paid', value: fallbackScheduled, perPieceValue: perPiece(fallbackScheduled), isEditable: false },
      { label: 'Actual', value: actualSeconds, perPieceValue: perPiece(actualSeconds), isEditable: true },
    ];
  }

  const scheduledTotal = batchData.scheduledCycleTimeTotalInSeconds;
  const idealTotal = batchData.idealCycleTimeTotalInSeconds;
  const paidTotal = batchData.paidCycleTimeTotalInSeconds;

  return [
    { label: 'Scheduled', value: scheduledTotal, perPieceValue: perPiece(scheduledTotal), isEditable: false },
    { label: 'Ideal', value: idealTotal, perPieceValue: perPiece(idealTotal), isEditable: false },
    { label: 'Paid', value: paidTotal, perPieceValue: perPiece(paidTotal), isEditable: false },
    { label: 'Actual', value: actualSeconds, perPieceValue: perPiece(actualSeconds), isEditable: true },
  ];
};

// --- Note content conversion helpers ---

export const plainTextToRtf = (text: string): string => {
  const escaped = text.replace(/\\/g, '\\\\').replace(/\{/g, '\\{').replace(/\}/g, '\\}');
  const paragraphs = escaped.split('\n').join('\\par ');
  return `{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0 Arial;}}{\\colortbl;}\\pard\\slmult1 ${paragraphs}}`;
};

export const rtfToPlainText = (rtf: string): string => {
  if (!rtf) return '';
  let text = rtf;
  text = text.replace(/\{\\fonttbl[^}]*\}/g, '');
  text = text.replace(/\{\\colortbl[^}]*\}/g, '');
  text = text.replace(/\{\\stylesheet[^}]*\}/g, '');
  text = text.replace(/\\par\b\s?/g, '\n');
  text = text.replace(/\\[a-z]+\d*\s?/gi, '');
  text = text.replace(/[{}]/g, '');
  return text.trim();
};
